import React, { useEffect, useState } from "react";
import TopNav from "@/components/TopNav";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/auth/AuthContext";
import { Navigate } from "react-router-dom";

export default function GroupsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create/Edit dialog
  const [showDialog, setShowDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const loadData = async () => {
    setLoading(true);
    try {
      const [groupsRes, usersRes] = await Promise.all([
        api.get("/groups"),
        api.get("/admin/users"),
      ]);
      setGroups(groupsRes.data.groups || []);
      setUsers(usersRes.data.users || []);
    } catch (e) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingGroup(null);
    setGroupName("");
    setGroupDescription("");
    setSelectedSupervisors([]);
    setSelectedMembers([]);
    setShowDialog(true);
  };

  const openEditDialog = (group) => {
    setEditingGroup(group);
    setGroupName(group.name || "");
    setGroupDescription(group.description || "");
    setSelectedSupervisors(group.supervisor_ids || []);
    setSelectedMembers(group.member_ids || []);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingGroup(null);
  };

  const handleSave = async () => {
    if (!groupName.trim()) {
      setError("Group name is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: groupName,
        description: groupDescription,
        supervisor_ids: selectedSupervisors,
        member_ids: selectedMembers,
      };

      if (editingGroup) {
        await api.put(`/groups/${editingGroup.group_id}`, payload);
      } else {
        await api.post("/groups", payload);
      }
      closeDialog();
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to save group");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingGroup) return;
    setDeleting(true);
    try {
      await api.delete(`/groups/${deletingGroup.group_id}`);
      setShowDeleteDialog(false);
      setDeletingGroup(null);
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to delete group");
    } finally {
      setDeleting(false);
    }
  };

  const toggleSupervisor = (userId) => {
    setSelectedSupervisors((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleMember = (userId) => {
    setSelectedMembers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const getUserName = (userId) => {
    const u = users.find((u) => u.user_id === userId);
    return u ? `${u.name || u.email}` : userId;
  };

  // Admins are excluded - they already have full access to all sessions
  const supervisorUsers = users.filter((u) => u.role === "supervisor");
  const memberUsers = users.filter((u) => u.role === "user");

  return (
    <div data-testid="groups-page" className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <TopNav />
      <div className="mx-auto max-w-5xl px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="groups-title">
              Groups
            </h1>
            <p className="mt-2 text-base text-slate-600">
              Manage teams and assign supervisors
            </p>
          </div>
          <Button
            data-testid="create-group-btn"
            onClick={openCreateDialog}
            className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
          >
            Create Group
          </Button>
        </div>

        {error && (
          <div data-testid="groups-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-slate-600">Loading...</div>
        ) : groups.length === 0 ? (
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardContent className="py-8 text-center text-slate-600">
              No groups created yet. Click Create Group to get started.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {groups.map((group) => (
              <Card key={group.group_id} data-testid={`group-card-${group.group_id}`} className="border-slate-200 bg-white shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle data-testid={`group-name-${group.group_id}`}>{group.name}</CardTitle>
                      {group.description && (
                        <p className="text-sm text-slate-600 mt-1">{group.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`edit-group-${group.group_id}`}
                        variant="secondary"
                        className="rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
                        onClick={() => openEditDialog(group)}
                      >
                        Edit
                      </Button>
                      <Button
                        data-testid={`delete-group-${group.group_id}`}
                        variant="secondary"
                        className="rounded-full bg-white border border-rose-300 text-rose-700 hover:bg-rose-50"
                        onClick={() => {
                          setDeletingGroup(group);
                          setShowDeleteDialog(true);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-2">
                        Supervisors ({group.supervisor_ids?.length || 0})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(group.supervisor_ids || []).length === 0 ? (
                          <span className="text-sm text-slate-500">None assigned</span>
                        ) : (
                          group.supervisor_ids.map((id) => (
                            <Badge key={id} variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                              {getUserName(id)}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-2">
                        Members ({group.member_ids?.length || 0})
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(group.member_ids || []).length === 0 ? (
                          <span className="text-sm text-slate-500">None assigned</span>
                        ) : (
                          group.member_ids.map((id) => (
                            <Badge key={id} variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">
                              {getUserName(id)}
                            </Badge>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent className="bg-white border-slate-200 sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingGroup ? "Edit Group" : "Create Group"}</DialogTitle>
              <DialogDescription>
                {editingGroup ? "Update group details and assignments" : "Create a new team group"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="group-name">Group Name *</Label>
                <Input
                  id="group-name"
                  data-testid="group-name-input"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="E.g. Delhi Team, Night Shift"
                  className="bg-white border-slate-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group-desc">Description (optional)</Label>
                <Input
                  id="group-desc"
                  data-testid="group-desc-input"
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  placeholder="Team description..."
                  className="bg-white border-slate-300"
                />
              </div>

              {/* Supervisors Selection */}
              <div className="space-y-2">
                <Label>Supervisors (can view this group&apos;s sessions)</Label>
                <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                  {supervisorUsers.length === 0 ? (
                    <div className="text-sm text-slate-500">No supervisors available. Create users with supervisor role first.</div>
                  ) : (
                    supervisorUsers.map((u) => (
                      <label key={u.user_id} className="flex items-center gap-2 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedSupervisors.includes(u.user_id)}
                          onChange={() => toggleSupervisor(u.user_id)}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <span className="text-sm">{u.name || u.email}</span>
                        <Badge variant="outline" className="text-xs">
                          {u.role}
                        </Badge>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Members Selection */}
              <div className="space-y-2">
                <Label>Members (users whose sessions supervisors can view)</Label>
                <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                  {memberUsers.length === 0 ? (
                    <div className="text-sm text-slate-500">No users available.</div>
                  ) : (
                    memberUsers.map((u) => (
                      <label key={u.user_id} className="flex items-center gap-2 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedMembers.includes(u.user_id)}
                          onChange={() => toggleMember(u.user_id)}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <span className="text-sm">{u.name || u.email}</span>
                        <Badge variant="outline" className="text-xs">
                          {u.role}
                        </Badge>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="secondary"
                className="rounded-full bg-white border border-slate-300 text-slate-800 hover:bg-slate-50"
                onClick={closeDialog}
              >
                Cancel
              </Button>
              <Button
                data-testid="save-group-btn"
                onClick={handleSave}
                disabled={saving}
                className="rounded-full bg-slate-900 text-white hover:bg-slate-800"
              >
                {saving ? "Saving..." : editingGroup ? "Save Changes" : "Create Group"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-white border-slate-200">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete group {deletingGroup?.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove the group. Users and supervisors will not be deleted, only their group assignment.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-rose-600 text-white hover:bg-rose-700"
              >
                {deleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
